import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Autocomplete, TextField, Box, Typography, CircularProgress } from '@mui/material';
import { getOrders } from '../app/api';
import { formatDateTime } from '../app/date';
import { useTranslation } from '../app/LanguageContext';

const FETCH_PAGE_SIZE = 25;
const DEBOUNCE_MS = 300;

/**
 * Autocomplete to pick an order by id. Searches the list API as the user types (q=…).
 * Options show number, title, customer, and creation date.
 */
export function OrderSearchAutocomplete({
  value,
  onChange,
  excludeOrderId,
  label,
  disabled = false,
  size = 'small',
  fullWidth = true,
  error = false,
  helperText = '',
  'data-testid': dataTestId,
  noOptionsText: noOptionsTextProp,
}) {
  const { translate } = useTranslation();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastQueryRef = useRef('');
  const requestSeq = useRef(0);
  const debounceRef = useRef(null);

  const applyExclude = useCallback(
    (rows) => {
      const ex = excludeOrderId != null && String(excludeOrderId).trim() !== '' ? String(excludeOrderId).trim() : null;
      if (!ex) return rows;
      return (rows || []).filter((o) => String(o.id) !== ex);
    },
    [excludeOrderId],
  );

  const fetchForQuery = useCallback(
    async (q) => {
      const seq = ++requestSeq.current;
      lastQueryRef.current = q;
      setLoading(true);
      try {
        const res = await getOrders({
          q,
          page: 0,
          page_size: FETCH_PAGE_SIZE,
          sort: 'created_at',
          order: 'desc',
        });
        if (seq !== requestSeq.current) return;
        setOptions(applyExclude(res.data?.orders || []));
      } catch {
        if (seq === requestSeq.current) setOptions([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [applyExclude],
  );

  const scheduleFetch = useCallback(
    (raw) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        void fetchForQuery(String(raw).trim());
      }, DEBOUNCE_MS);
    },
    [fetchForQuery],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  useEffect(() => {
    setOptions((prev) => applyExclude(prev));
  }, [excludeOrderId, applyExclude]);

  const handleOpen = useCallback(() => {
    if (options.length === 0 && !loading) {
      void fetchForQuery(lastQueryRef.current || '');
    }
  }, [options.length, loading, fetchForQuery]);

  return (
    <Autocomplete
      data-testid={dataTestId}
      fullWidth={fullWidth}
      size={size}
      options={options}
      loading={loading}
      value={value}
      onChange={(_e, newValue) => onChange(newValue ?? null)}
      onOpen={handleOpen}
      onInputChange={(_e, newInput, reason) => {
        if (reason === 'clear') {
          onChange(null);
          void fetchForQuery('');
          return;
        }
        if (reason === 'input') {
          scheduleFetch(newInput);
        }
      }}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      getOptionLabel={(o) => {
        if (!o || !o.number) return '';
        return `#${o.number} — ${(o.customer_name || '').trim() || '—'}`;
      }}
      filterOptions={(opts) => opts}
      noOptionsText={
        noOptionsTextProp != null
          ? noOptionsTextProp
          : translate('orders.order_search_no_options') || 'Nenhum pedido'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          disabled={disabled}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, o) => (
        <li {...props}>
          <Box sx={{ display: 'block' }}>
            <Typography component="div" variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              #{o.number}
              {o.title ? ` — ${o.title}` : ''}
            </Typography>
            <Typography component="div" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {(o.customer_name || '—') + ' · ' + (formatDateTime(o.created_at) || '—')}
            </Typography>
          </Box>
        </li>
      )}
    />
  );
}
